import { useMutation, useQuery, useQueryClient } from 'react-query';

import { APIList, StringifiedUUID, SourceType } from 'types';
import { SchemaDiff } from 'types/dataObjectSchemas';
import {
  Workbook,
  GetWorksheetResponse,
  GetSnapshotResponse,
  Worksheet,
  ListDataWorksheetQueryParams,
  CreateDataWorkbookRequest,
  CreateDataWorksheetRequest,
  UpdateWorksheetSchemaRequest,
  UpdateWorksheetSchemaResponse,
  ConnectorDestinationColumn,
  ListTransformationWorksheetQueryParams,
} from 'types/worksheets';
import { APIError } from 'utils/errors';

import { getRequest, postRequest } from './apiRequests';

const createWorkbookUrl = '/api/workbooks';
const updateSchemaUrl = '/api/v1/worksheets/workbooks';
const workbooksUrl = '/api/v1/data-workbooks/';
const transformationWorkbooksUrl = '/api/v1/transformation-workbooks/';
const worksheetUrl = '/api/v1/data-worksheets/';
const txnWorksheetUrl = '/api/v1/transformation-worksheets/';

const getSnapshotByPlanningCycleUrl = (planningCycleId: StringifiedUUID) =>
  `/api/v1/spm/planning-cycles/${planningCycleId}/data-snapshot-reference/`;

const createDataWorkbook = (csrfToken: string) => (workbookName: string) => {
  return postRequest<CreateDataWorkbookRequest, Workbook>(createWorkbookUrl, { name: workbookName }, csrfToken);
};

const createDataWorksheet =
  (csrfToken: string) =>
  ({ workbookId, ...request }: CreateDataWorksheetRequest) => {
    return postRequest<Omit<CreateDataWorksheetRequest, 'workbookId'>, Worksheet>(
      `${createWorkbookUrl}/${workbookId}/api_worksheets`,
      request,
      csrfToken,
    );
  };

const buildUpdateSchemaUrl = (workbookId: StringifiedUUID, worksheetId: StringifiedUUID) => {
  return `${updateSchemaUrl}/${workbookId}/worksheets/${worksheetId}/bulk-schema-v2/`;
};

const updateDataWorksheetSchema = (
  csrfToken: string,
  { workbookId, worksheetId, ...newSchema }: UpdateWorksheetSchemaRequest,
) => {
  return postRequest<Omit<UpdateWorksheetSchemaRequest, 'workbookId' | 'worksheetId'>, UpdateWorksheetSchemaResponse>(
    buildUpdateSchemaUrl(workbookId, worksheetId),
    newSchema,
    csrfToken,
  );
};

const updatePublishedWorksheetSchema = async (
  csrfToken: string,
  destinationWorkbookId: StringifiedUUID,
  destinationWorksheet: Worksheet,
  schemaDiff: SchemaDiff,
  columnOrder: string[],
): Promise<UpdateWorksheetSchemaResponse> => {
  const deletes: StringifiedUUID[] = schemaDiff.remove;
  const updateSchemaRequest: UpdateWorksheetSchemaRequest = {
    deletes,
    column_order: columnOrder,
    upserts: [],
    workbookId: destinationWorkbookId,
    worksheetId: destinationWorksheet.id,
  };

  // Add new columns
  schemaDiff.add.forEach((dataField) => {
    const column: ConnectorDestinationColumn = {
      name: dataField.name,
      variant: 'raw',
      data_type: dataField.data_type === 'picklist' ? 'string' : dataField.data_type,
      deleted: false,
      description: '',
    };
    if (dataField.data_type === 'number') {
      column.format = dataField.format;
    }
    updateSchemaRequest.upserts.push(column);
  });

  // Update existing columns
  schemaDiff.update.forEach((worksheetColumnId, dataField) => {
    const column: ConnectorDestinationColumn = {
      id: worksheetColumnId,
      name: dataField.name,
      variant: 'raw',
      data_type: dataField.data_type === 'picklist' ? 'string' : dataField.data_type,
      deleted: false,
      description: '',
    };
    if (dataField.data_type === 'number') {
      column.format = dataField.format;
    }
    updateSchemaRequest.upserts.push(column);
  });

  return updateDataWorksheetSchema(csrfToken, updateSchemaRequest);
};

const useUpdateDataWorksheetSchema = (csrfToken: string) => {
  return {
    mutation: useMutation<any, APIError, UpdateWorksheetSchemaRequest>((request) =>
      updateDataWorksheetSchema(csrfToken, request),
    ),
  };
};

const listDataWorksheets = (queryParams: ListDataWorksheetQueryParams) =>
  getRequest<ListDataWorksheetQueryParams, APIList<Workbook>>(workbooksUrl, queryParams);
const listTransformationWorksheets = (queryParams: ListTransformationWorksheetQueryParams) =>
  getRequest<ListTransformationWorksheetQueryParams, APIList<Workbook>>(transformationWorkbooksUrl, queryParams);
const getDataWorksheet = (worksheetId: StringifiedUUID) =>
  getRequest<null, GetWorksheetResponse>(`${worksheetUrl}${worksheetId}/`);
const getTxnWorksheet = (worksheetId: StringifiedUUID) =>
  getRequest<null, GetWorksheetResponse>(`${txnWorksheetUrl}${worksheetId}/`);

const getSnapshotByPlanningCycleId = (planningCycleId: StringifiedUUID) => {
  const url = getSnapshotByPlanningCycleUrl(planningCycleId);
  return getRequest<null, GetSnapshotResponse>(url);
};

const useGetSnapshotStatus = (planningCycleId: StringifiedUUID, enabled: boolean) => {
  const QUERY_KEY = ['snapshot-by-worksheet', planningCycleId];
  const queryClient = useQueryClient();

  const query = useQuery<GetSnapshotResponse, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getSnapshotByPlanningCycleId(planningCycleId),
    enabled,
  });
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const useGetDataWorksheets = (workbookName?: string) => {
  const QUERY_KEY = ['data-worksheets'];
  const queryClient = useQueryClient();
  const queryParams: ListDataWorksheetQueryParams = { limit: 1000 };
  if (workbookName) {
    QUERY_KEY.push(workbookName);
    queryParams.name_contains = workbookName;
  }

  const query = useQuery<APIList<Workbook>, APIError>(QUERY_KEY, () => listDataWorksheets(queryParams));
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const useGetTransformationWorksheets = (workbookName?: string) => {
  const QUERY_KEY = ['transformation-worksheets'];
  const queryClient = useQueryClient();
  const queryParams: ListTransformationWorksheetQueryParams = { limit: 1000 };
  if (workbookName) {
    QUERY_KEY.push(workbookName);
    queryParams.name_contains = workbookName;
  }
  const query = useQuery<APIList<Workbook>, APIError>(QUERY_KEY, () => listTransformationWorksheets(queryParams));
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

// eslint-disable-next-line @typescript-eslint/default-param-last
const useGetDataWorksheet = (worksheetId: StringifiedUUID, sourceType: string = SourceType.DATA, enabled: boolean) => {
  const QUERY_KEY = ['data-worksheet', worksheetId];
  const queryClient = useQueryClient();
  const queryFxn = sourceType === SourceType.DATA ? getDataWorksheet : getTxnWorksheet;
  const query = useQuery<GetWorksheetResponse, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => queryFxn(worksheetId),
    enabled,
  });
  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

export const DataWorksheetService = {
  apiUrl: workbooksUrl,
  dataWorksheetApiUrl: worksheetUrl,
  buildUpdateSchemaUrl,
  getSnapshotByPlanningCycleUrl,
  getDataWorksheet,
  useGetDataWorksheets,
  useGetDataWorksheet,
  useGetSnapshotStatus,
  useUpdateDataWorksheetSchema,
  createDataWorkbook,
  createDataWorksheet,
  listDataWorksheets,
  updateDataWorksheetSchema,
  updatePublishedWorksheetSchema,
  useGetTransformationWorksheets,
};
